import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/useApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { RotateCcw, Copy, Key, CheckCircle2, XCircle } from "lucide-react";

const ApiUrlField = ({ api, product }) => {
  const qc = useQueryClient();
  const [value, setValue] = useState(product.api_base_url || "");
  const save = useMutation({
    mutationFn: async () => (await api.patch(`/admin/products/${product.id}/api-url`, { api_base_url: value })).data,
    onSuccess: (r) => { setValue(r.api_base_url); qc.invalidateQueries({ queryKey: ["admin-api-keys"] }); qc.invalidateQueries({ queryKey: ["admin-products"] }); toast.success("Indirizzo API salvato"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const dirty = value.trim().replace(/\/+$/, "") !== (product.api_base_url || "");
  return (
    <div className="mt-4">
      <label className="text-xs font-semibold text-slate-600">Indirizzo API dell'app (base URL)</label>
      <div className="flex gap-2 mt-1">
        <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="https://api.tuaapp.it" className="font-mono text-sm" data-testid={`api-url-${product.slug}`} />
        <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()} className="bg-[#B84A2B] hover:bg-[#983C21] text-white" data-testid={`save-api-url-${product.slug}`}>Salva</Button>
      </div>
    </div>
  );
};

const IntegrationLog = ({ api }) => {
  const qc = useQueryClient();
  const retry = useMutation({
    mutationFn: async (cid) => (await api.post(`/admin/customers/${cid}/retry-activation`)).data,
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["admin-integration-log"] }); r.ok ? toast.success(`Attivazione riuscita: ${r.external_account_id}`) : toast.error(`Ancora non riuscita: ${r.error || "errore"}`); },
    onError: (e) => { qc.invalidateQueries({ queryKey: ["admin-integration-log"] }); toast.error(e.response?.data?.detail || "Errore"); },
  });
  const { data = [] } = useQuery({
    queryKey: ["admin-integration-log"],
    queryFn: async () => (await api.get("/admin/integration-log")).data,
  });
  return (
    <div className="bg-white border border-[#E2DDD5] rounded-xl p-5" data-testid="integration-log">
      <div className="font-semibold">Registro attivazioni sulle app</div>
      <p className="text-xs text-slate-500 mt-1">Se l'app non risponde il cliente viene attivato comunque nel portale e qui resta l'errore.</p>
      <div className="overflow-x-auto mt-3">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-500 bg-[#F2F0EB]">
            <tr><th className="text-left px-3 py-2">Data</th><th className="text-left px-3 py-2">Prodotto</th><th className="text-left px-3 py-2">Cliente</th><th className="text-left px-3 py-2">Esito</th><th className="text-left px-3 py-2">ID account</th></tr>
          </thead>
          <tbody>
            {data.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-500">Nessuna attivazione registrata.</td></tr>}
            {data.map((l) => (
              <tr key={l.id} className="border-t border-[#E2DDD5]" data-testid={`log-${l.id}`}>
                <td className="px-3 py-2 text-slate-500">{new Date(l.created_at).toLocaleString("it-IT")}</td>
                <td className="px-3 py-2">{l.product_name || "—"}</td>
                <td className="px-3 py-2">{l.customer_name || "—"}</td>
                <td className="px-3 py-2">{l.ok ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={14} /> Riuscita</span> : <span className="text-rose-700 inline-flex items-center gap-1" title={l.detail || l.error || ""}><XCircle size={14} /> {l.ok === false ? "Non riuscita" : "Simulata"}</span>}{l.error && <div className="text-[11px] text-slate-500 max-w-xs truncate">{l.error}</div>}{!l.ok && l.detail && <div className="text-[11px] text-slate-600 max-w-md break-words font-mono bg-slate-50 border border-slate-200 rounded px-1.5 py-1 mt-1" data-testid={`log-detail-${l.id}`}>Risposta dell'app: {l.detail}</div>}{l.can_retry && <Button size="sm" variant="outline" className="mt-1 h-7 text-xs" disabled={retry.isPending} onClick={() => retry.mutate(l.customer_id)} data-testid={`btn-retry-${l.id}`}><RotateCcw size={12} className="mr-1" /> Riprova attivazione</Button>}</td>
                <td className="px-3 py-2 font-mono text-xs">{l.external_account_id}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function AdminApiKeys() {
  const api = useApi();
  const qc = useQueryClient();
  const { data = [] } = useQuery({
    queryKey: ["admin-api-keys"],
    queryFn: async () => (await api.get("/admin/api-keys")).data,
  });
  const rotate = useMutation({
    mutationFn: async (id) => (await api.post(`/admin/products/${id}/rotate-key`)).data,
    onMutate: (id) => { if (!window.confirm("Rigenerare la chiave? L'app collegata smetterà di funzionare finché non riceve la nuova chiave.")) throw new Error("cancelled"); return id; },
    onError: (e) => { if (e.message !== "cancelled") toast.error(e.response?.data?.detail || "Errore"); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-api-keys"] }); toast.success("Chiave rigenerata"); },
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Chiavi API prodotti</h1>
        <p className="text-sm text-slate-500 mt-1">Chiavi univoche per autenticare Booking Hub, Cantiere 360 Cloud e futuri prodotti sulle API esterne del portale.</p>
      </div>
      <div className="space-y-3">
        {data.map((p) => (
          <div key={p.id} className="bg-white border border-[#E2DDD5] rounded-xl p-5" data-testid={`apikey-${p.slug}`}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#1E2A38] text-white flex items-center justify-center"><Key size={16} /></div>
                <div>
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-xs text-slate-500">Chiave del portale: l'app la usa per chiamare il portale (rinnovi, cambi piano, disattivazioni). La chiave dell'app si imposta in Prodotti.</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <code className="font-mono text-xs bg-slate-900 text-slate-100 px-3 py-2 rounded break-all max-w-md">{p.api_key || "— non generata —"}</code>
                <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(p.api_key || ""); toast.success("Copiata"); }} data-testid={`copy-${p.slug}`}><Copy size={14} /></Button>
                <Button variant="outline" size="sm" onClick={() => rotate.mutate(p.id)} data-testid={`rotate-${p.slug}`}><RotateCcw size={14} className="mr-1" /> Ruota</Button>
              </div>
            </div>
            <ApiUrlField api={api} product={p} />
            <div className="mt-3 text-xs text-slate-500">
              Headers richiesti: <code className="font-mono bg-slate-100 px-1 rounded">X-Product-Api-Key: {`<key>`}</code> ·
              Endpoints: <code className="font-mono bg-slate-100 px-1 rounded">/api/external/renewals</code>, <code className="font-mono bg-slate-100 px-1 rounded">/api/external/plan-change</code>, <code className="font-mono bg-slate-100 px-1 rounded">/api/external/deactivation</code>
              <br />Attivazione (portale → app): <code className="font-mono bg-slate-100 px-1 rounded">{(() => { const a = (p.endpoints || []).find((e) => e.purpose === "activation") || { method: "POST", path: "/accounts" }; return `${a.method} ${p.api_base_url || "<api base url>"}${a.path}`; })()}</code> con header <code className="font-mono bg-slate-100 px-1 rounded">X-Portal-Api-Key</code> (stessa chiave) — dettagli in <a href="/developer" className="text-[#B84A2B] hover:underline">Documentazione API</a>.
            </div>
          </div>
        ))}
      </div>
      <IntegrationLog api={api} />
    </div>
  );
}
